import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useMemo, useState, useEffect, useRef } from "react";
import styles from "./index.module.css";
import ReportItemPlatinumPlus from "Reporting/Components/ReportItemPlatinumPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { Select } from "shared/Select/Select";

const PAGE_HEIGHT_PX = 425;

const STATIC_COLUMNS = [
  { id: "ibo", label: "IBO", type: "text", sortable: true, width: 6 },
  {
    id: "lastName",
    label: "Last Name",
    type: "text",
    sortable: true,
    width: 5,
  },
  {
    id: "firstName",
    label: "First Name",
    type: "text",
    sortable: true,
    width: 5,
  },
  {
    id: "diamond",
    label: "Diamond",
    type: "text",
    sortable: true,
    filterable: true,
    width: 6,
  },
  {
    id: "pin",
    label: "Pin Level",
    type: "text",
    sortable: true,
    filterable: true,
    width: 6,
  },
  { id: "users", label: "Users", type: "number", sortable: true, width: 4 },
  {
    id: "inPerson",
    label: "In-Person",
    type: "number",
    sortable: true,
    width: 5,
  },
  {
    id: "virtual",
    label: "Virtual",
    type: "number",
    sortable: true,
    width: 5,
  },
];

const STATIC_COLUMNS_WIDTH = STATIC_COLUMNS.reduce(
  (sum, col) => sum + (Number(col.width) || 0),
  0
);
const DYNAMIC_COLUMNS_WIDTH = 100 - STATIC_COLUMNS_WIDTH;

const getDynamicColumnId = (groupName, columnName) =>
  `${groupName}::${columnName}`;

const getDynamicValue = (row, groupName, columnName) =>
  Number(row?.values?.[groupName]?.[columnName] || 0);

const compareValues = (a, b, direction, type = "text") => {
  const dir = direction === "asc" ? 1 : -1;

  if (type === "number") {
    const aNum = Number(a) || 0;
    const bNum = Number(b) || 0;
    if (aNum < bNum) return -1 * dir;
    if (aNum > bNum) return 1 * dir;
    return 0;
  }

  const aStr = (a ?? "").toString().toLowerCase();
  const bStr = (b ?? "").toString().toLowerCase();

  if (aStr < bStr) return -1 * dir;
  if (aStr > bStr) return 1 * dir;
  return 0;
};

export default function ReportTablePlatinumPlus({
  report,
  title,
  onSortedReportsChange,
  selectedColumns,
  onSelectedColumnsChange,
}) {
  const tableRef = useRef(null);

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([]);

  const { groupedHeaders, allDynamicColumns } = useMemo(() => {
    const grouped =
      (report?.columnGroups || [])
        .slice()
        .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
        .map((group) => {
          const columns = (group.columns || [])
            .slice()
            .sort((a, b) => {
              const sortDiff = (Number(a.sort) || 0) - (Number(b.sort) || 0);
              if (sortDiff !== 0) return sortDiff;
              return (a.name || "").localeCompare(b.name || "");
            })
            .map((column) => ({
              id: getDynamicColumnId(group.header, column.name),
              field: getDynamicColumnId(group.header, column.name),
              groupName: group.header,
              name: column.name,
              headerName: column.name,
              type: "number",
              sortable: true,
            }));

          return {
            headerName: group.header,
            columns,
            colSpan: columns.length,
          };
        }) || [];

    return {
      groupedHeaders: grouped,
      allDynamicColumns: grouped.flatMap((group) => group.columns),
    };
  }, [report]);

  const visibleDynamicColumns = useMemo(() => {
    return allDynamicColumns.filter((col) => selectedColumns?.includes(col.id));
  }, [allDynamicColumns, selectedColumns]);

  const visibleGroupedHeaders = useMemo(() => {
    return groupedHeaders
      .map((group) => ({
        ...group,
        columns: group.columns.filter((col) =>
          selectedColumns?.includes(col.id)
        ),
      }))
      .filter((group) => group.columns.length > 0);
  }, [groupedHeaders, selectedColumns]);

  const totalVisibleDynamicColumnCount = visibleDynamicColumns.length || 1;

  const prevColumnKeyRef = useRef("");

  useEffect(() => {
    if (!allDynamicColumns.length) return;

    const columnKey = allDynamicColumns.map((c) => c.id).join("|");

    if (columnKey !== prevColumnKeyRef.current) {
      onSelectedColumnsChange?.(
        title,
        allDynamicColumns.map((c) => c.id)
      );
      prevColumnKeyRef.current = columnKey;
    }
  }, [allDynamicColumns, onSelectedColumnsChange, title]);

  const getAllOptions = (field) => {
    const rows = report?.rows || [];

    if (field === "pin") {
      return Array.from(new Set(rows.map((r) => r.pin).filter(Boolean))).sort();
    }

    if (field === "diamond") {
      return Array.from(
        new Set(rows.map((r) => r.diamond).filter(Boolean))
      ).sort();
    }

    return [];
  };

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const initial = {};

    const pinOptions = getAllOptions("pin");
    if (pinOptions.length) initial.pin = [...pinOptions];

    const diamondOptions = getAllOptions("diamond");
    if (diamondOptions.length) initial.diamond = [...diamondOptions];

    return initial;
  });

  useEffect(() => {
    setSelectedFilters((prev) => {
      const next = { ...prev };

      const syncField = (field) => {
        const options = getAllOptions(field);

        if (!options.length) {
          delete next[field];
          return;
        }

        const prevSelected = Array.isArray(prev[field]) ? prev[field] : [];
        const kept = prevSelected.filter((value) => options.includes(value));
        const added = options.filter((value) => !prevSelected.includes(value));

        next[field] = [...kept, ...added];
      };

      syncField("pin");
      syncField("diamond");

      return next;
    });
  }, [report]);

  const [sortedRows, setSortedRows] = useState(report?.rows || []);
  const [totalItem, setTotalItem] = useState({
    ibo: "Totals:",
    lastName: "",
    firstName: "",
    diamond: "",
    pin: "",
    users: "",
    inPerson: "",
    virtual: "",
    values: {},
  });

  const updateFilter = (field, value) => {
    setSelectedFilters((prev) => {
      const current = prev[field] || [];
      const allOptions = getAllOptions(field);

      let newValues;
      if (value === "All") {
        newValues = current.length === allOptions.length ? [] : [...allOptions];
      } else {
        if (current.includes(value)) {
          newValues = current.filter((v) => v !== value);
        } else {
          newValues = [...current, value];
        }

        if (newValues.length === allOptions.length) {
          newValues = [...allOptions];
        }
      }

      newValues = newValues.filter((v) => v !== "All");

      return {
        ...prev,
        [field]: newValues,
      };
    });
  };

  const toggleSort = (field, type = "text") => {
    setSort((prev) => {
      const existingIndex = prev.findIndex((s) => s.field === field);
      let newSort;

      if (existingIndex === -1) {
        newSort = [...prev, { field, direction: "asc", type }];
      } else {
        const existing = prev[existingIndex];
        let nextDirection;

        if (existing.direction === "asc") nextDirection = "desc";
        else if (existing.direction === "desc") nextDirection = "none";
        else nextDirection = "asc";

        if (nextDirection === "none") {
          newSort = prev.filter((_, i) => i !== existingIndex);
        } else {
          newSort = [...prev];
          newSort[existingIndex] = {
            field,
            direction: nextDirection,
            type,
          };
        }
      }

      return newSort;
    });
  };

  const renderSortIcon = (field) => {
    const index = sort.findIndex((s) => s.field === field);

    if (index === -1 || sort[index].direction === "none") {
      return <FontAwesomeIcon icon={faMinus} className={styles.menuChevron} />;
    }

    const icon = sort[index].direction === "asc" ? faChevronUp : faChevronDown;

    return (
      <div className={styles.sortWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.menuChevron} />
        <span className={styles.sortPriority}>{index + 1}</span>
      </div>
    );
  };

  useEffect(() => {
    let result = Array.isArray(report?.rows) ? [...report.rows] : [];

    const hasEmptyFilter = Object.values(selectedFilters).some(
      (values) => Array.isArray(values) && values.length === 0
    );

    if (hasEmptyFilter) {
      setSortedRows([]);
      return;
    }

    Object.entries(selectedFilters).forEach(([field, values]) => {
      if (!values || !values.length) return;

      if (field === "pin" || field === "diamond") {
        result = result.filter((row) => values.includes(row[field]));
      }
    });

    result = result.filter((row) => {
      if (!visibleDynamicColumns.length) return false;

      return visibleDynamicColumns.some(
        (col) => getDynamicValue(row, col.groupName, col.name) !== 0
      );
    });

    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const sortItem of sort) {
          const { field, direction, type } = sortItem;
          if (!field || direction === "none") continue;

          let aValue;
          let bValue;

          if (field.includes("::")) {
            const [groupName, columnName] = field.split("::");
            aValue = getDynamicValue(a, groupName, columnName);
            bValue = getDynamicValue(b, groupName, columnName);
          } else {
            aValue = a[field];
            bValue = b[field];
          }

          const comparison = compareValues(aValue, bValue, direction, type);
          if (comparison !== 0) return comparison;
        }

        return 0;
      });
    }

    setSortedRows(result);
  }, [report, sort, selectedFilters, visibleDynamicColumns]);

  useEffect(() => {
    onSortedReportsChange?.(title, sortedRows);
  }, [sortedRows, title, onSortedReportsChange]);

  useEffect(() => {
    const totalsValues = {};

    visibleDynamicColumns.forEach((col) => {
      if (!totalsValues[col.groupName]) totalsValues[col.groupName] = {};

      totalsValues[col.groupName][col.name] = sortedRows.reduce(
        (sum, row) => sum + getDynamicValue(row, col.groupName, col.name),
        0
      );
    });

    setTotalItem({
      ibo: "Totals:",
      lastName: "",
      firstName: "",
      diamond: "",
      pin: "",
      users: "",
      inPerson: "",
      virtual: "",
      values: totalsValues,
    });
  }, [sortedRows, visibleDynamicColumns]);

  const calculateBreaks = (ref, setBreaks) => {
    if (!ref.current) return;

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const upperHeaderEl = ref.current.querySelector(`.${styles.upperHeader}`);
      const titleEl = ref.current.parentElement?.querySelector(
        `.${styles.headerInfo}`
      );

      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      const upperHeaderHeight =
        upperHeaderEl?.getBoundingClientRect().height || 0;
      const titleHeight = titleEl?.getBoundingClientRect().height || 0;

      const staticHeight = headerHeight + upperHeaderHeight + titleHeight;

      let heightSoFar = staticHeight;
      let currentRowIndex = 0;

      allElements.forEach((el) => {
        const rect = el.getBoundingClientRect();

        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          heightSoFar = staticHeight + rect.height;
        } else {
          heightSoFar += rect.height;
        }

        currentRowIndex++;
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(tableRef, setPageBreakIndexes);
  }, [sortedRows, totalItem]);

  const totalDynamicColumnCount = allDynamicColumns.length || 1;

  return (
    <div>
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>{title}</div>
        </div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.upperHeader}>
            <div
              className={styles.upperHeaderFiller}
              style={{
                flex: `0 0 ${STATIC_COLUMNS_WIDTH}%`,
                maxWidth: `${STATIC_COLUMNS_WIDTH}%`,
              }}
            />
            <div
              className={styles.dynamicHeaderBox}
              style={{
                flex: `0 0 ${DYNAMIC_COLUMNS_WIDTH}%`,
                maxWidth: `${DYNAMIC_COLUMNS_WIDTH}%`,
              }}
            >
              {groupedHeaders.map((header) => {
                const widthPercent =
                  allDynamicColumns.length > 0
                    ? (header.columns.length / allDynamicColumns.length) * 100
                    : 0;

                const selectedCount = header.columns.filter((col) =>
                  selectedColumns?.includes(col.id)
                ).length;

                const isGroupSelected =
                  header.columns.length > 0 &&
                  selectedCount === header.columns.length;

                const isGroupDimmed = selectedCount === 0;

                return (
                  <div
                    key={header.headerName}
                    className={styles.headerGroup}
                    style={{
                      flex: `0 0 ${widthPercent}%`,
                      maxWidth: `${widthPercent}%`,
                      opacity: isGroupDimmed ? 0.4 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      className={styles.headerGroupCheckbox}
                      checked={isGroupSelected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const newSelected = [...(selectedColumns || [])];

                        header.columns.forEach((col) => {
                          if (checked && !newSelected.includes(col.id)) {
                            newSelected.push(col.id);
                          } else if (!checked) {
                            const index = newSelected.indexOf(col.id);
                            if (index > -1) newSelected.splice(index, 1);
                          }
                        });

                        onSelectedColumnsChange?.(title, newSelected);
                      }}
                    />

                    <span className={styles.headerLabel}>
                      {header.headerName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.header}>
            {STATIC_COLUMNS.map((col) => {
              const content = col.filterable ? (
                <div
                  className={styles.selectContainer}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Select
                    options={["All", ...getAllOptions(col.id)]}
                    placeholder={
                      !selectedFilters[col.id] ||
                      selectedFilters[col.id].length === 0
                        ? col.label
                        : selectedFilters[col.id].length ===
                          getAllOptions(col.id).length
                        ? col.label
                        : selectedFilters[col.id].join(", ")
                    }
                    onChange={(value) => updateFilter(col.id, value)}
                    selectedValues={selectedFilters[col.id] || []}
                  />
                </div>
              ) : (
                <span>{col.label}</span>
              );

              return (
                <div
                  key={col.id}
                  className={classNames(
                    styles.staticHeaderCell,
                    styles[`col_${col.id}`]
                  )}
                  style={{
                    flex: `0 0 ${col.width}%`,
                    maxWidth: `${col.width}%`,
                  }}
                >
                  <div
                    className={styles.headerCellInner}
                    onClick={() => col.sortable && toggleSort(col.id, col.type)}
                  >
                    {content}
                    {col.sortable && renderSortIcon(col.id)}
                  </div>
                </div>
              );
            })}

            <div
              className={styles.dynamicColumnsBox}
              style={{
                flex: `0 0 ${DYNAMIC_COLUMNS_WIDTH}%`,
                maxWidth: `${DYNAMIC_COLUMNS_WIDTH}%`,
              }}
            >
              {allDynamicColumns.map((col) => {
                const isSelected = selectedColumns?.includes(col.id);

                return (
                  <div
                    key={col.id}
                    className={classNames(
                      styles.col,
                      groupedHeaders.some(
                        (group) => group.columns[0]?.id === col.id
                      ) && styles.firstDynamicInGroup
                    )}
                    style={{
                      width: `${100 / totalDynamicColumnCount}%`,
                      opacity: isSelected ? 1 : 0.4,
                      cursor: isSelected ? "pointer" : "default",
                    }}
                  >
                    <div className={styles.colHeaderBottom}>
                      <span className={styles.colHeaderLabel}>
                        {col.headerName}
                      </span>

                      <div className={styles.colHeaderControls}>
                        <input
                          type="checkbox"
                          className={styles.columnCheckbox}
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onSelectedColumnsChange?.(title, [
                                ...(selectedColumns || []),
                                col.id,
                              ]);
                            } else {
                              onSelectedColumnsChange?.(
                                title,
                                (selectedColumns || []).filter(
                                  (id) => id !== col.id
                                )
                              );
                            }
                          }}
                        />

                        {isSelected && (
                          <span
                            className={styles.colSortIcon}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSort(col.field, "number");
                            }}
                          >
                            {renderSortIcon(col.field)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {totalItem && (
            <React.Fragment key="total">
              {pageBreakIndexes.includes(0) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemPlatinumPlus
                  item={totalItem}
                  isOdd={false}
                  type="total"
                  staticColumns={STATIC_COLUMNS}
                  allColumns={allDynamicColumns}
                  groupedHeaders={groupedHeaders}
                  dynamicWidth={DYNAMIC_COLUMNS_WIDTH}
                  selectedColumns={selectedColumns}
                />
              </div>
            </React.Fragment>
          )}

          {sortedRows.map((row, i) => (
            <React.Fragment key={`${row.ibo}-${i}`}>
              {pageBreakIndexes.includes(i + 1) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemPlatinumPlus
                  item={row}
                  isOdd={i % 2 !== 0}
                  type="row"
                  staticColumns={STATIC_COLUMNS}
                  allColumns={allDynamicColumns}
                  groupedHeaders={groupedHeaders}
                  dynamicWidth={DYNAMIC_COLUMNS_WIDTH}
                  selectedColumns={selectedColumns}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTablePlatinumPlus.propTypes = {
  report: PropTypes.shape({
    reportTitle: PropTypes.string,
    columnGroups: PropTypes.array,
    rows: PropTypes.array,
  }),
  title: PropTypes.string,
  onSortedReportsChange: PropTypes.func,
};
